import { useCallback, useEffect, useState } from 'react'
import { get, post } from '../lib/api'
import { t } from '../lib/i18n'
import type { User } from '../lib/auth'
import Icon from './Icon'
import { carPhoto, type Car } from './Garage'

/**
 * Recording a race, in two taps.
 *
 * Winner first, then who it beat. Deliberately that order: it is the order the
 * result is said out loud in the room ("Scar beat Twin Mill"), and asking for
 * the loser first means translating the sentence before entering it.
 *
 * Both cars are big tap targets with their photographs on them, because the
 * person entering this is usually seven and is holding a car.
 */

interface Match {
  id: string
  winner_name: string
  loser_name: string
  source: string
  created_at: number
  voided_at: number | null
}

export default function RecordMatch({ user }: { user: User }) {
  const [cars, setCars] = useState<Car[]>([])
  const [matches, setMatches] = useState<Match[]>([])
  const [winner, setWinner] = useState<Car | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const mayVoid = user.capabilities.includes('operations.write')

  const load = useCallback(async () => {
    try {
      const [garage, recent] = await Promise.all([
        get<{ cars: Car[] }>('/api/cars'),
        get<{ matches: Match[] }>('/api/matches?limit=15'),
      ])
      setCars(garage.cars)
      setMatches(recent.matches)
    } catch (e) {
      setError(e instanceof Error ? e.message : t('error.generic'))
    }
  }, [])

  useEffect(() => { void load() }, [load])

  async function pickLoser(loser: Car) {
    if (!winner) return
    setBusy(true)
    setError(null)
    try {
      await post('/api/matches', { winnerId: winner.id, loserId: loser.id })
      setDone(`${winner.name} ${t('race.beat')} ${loser.name}`)
      setWinner(null)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : t('error.generic'))
    } finally {
      setBusy(false)
    }
  }

  async function voidMatch(id: string) {
    try {
      await post(`/api/matches/${id}/void`, {})
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : t('error.generic'))
    }
  }

  function CarButton({ car, onPick }: { car: Car; onPick: (car: Car) => void }) {
    return (
      <button type="button" className="pick" disabled={busy} onClick={() => onPick(car)}>
        {carPhoto(car)
          ? <img src={carPhoto(car)!} alt="" loading="lazy" />
          : <span className="pick-empty"><Icon name="car" size={26} /></span>}
        <span className="pick-name">{car.name}</span>
        <span className="muted small">{car.wins}–{car.losses}</span>
      </button>
    )
  }

  return (
    <section className="card">
      <h1>{t('race.title')}</h1>
      {error && <p className="error" role="alert">{error}</p>}
      {done && <p className="ok" role="status">{done}</p>}

      {cars.length < 2 ? (
        <p className="muted">{t('race.needTwo')}</p>
      ) : !winner ? (
        <>
          <h2>{t('race.whoWon')}</h2>
          <div className="picker">
            {cars.map((car) => <CarButton key={car.id} car={car} onPick={(c) => { setWinner(c); setDone(null) }} />)}
          </div>
        </>
      ) : (
        <>
          <h2>
            {t('race.whoLost')}
            <button type="button" className="link" onClick={() => setWinner(null)}>{t('race.change')}</button>
          </h2>
          <p className="muted small">{winner.name} {t('race.beat')}…</p>
          <div className="picker">
            {/* The winner is not in this list: a car cannot beat itself, and an
                option that always fails is not an option. */}
            {cars.filter((c) => c.id !== winner.id).map((car) => (
              <CarButton key={car.id} car={car} onPick={(c) => void pickLoser(c)} />
            ))}
          </div>
        </>
      )}

      <h2>{t('race.recent')}</h2>
      {matches.length === 0 ? (
        <p className="muted">—</p>
      ) : (
        <ul className="list">
          {matches.map((m) => (
            <li key={m.id} className={m.voided_at ? 'is-voided' : ''}>
              <div>
                <strong>{m.winner_name}</strong> <span className="muted">{t('race.beat')}</span> {m.loser_name}
                <div className="muted small">
                  {new Date(m.created_at).toLocaleString()}
                  {m.source === 'tournament' && ' · bracket'}
                  {m.voided_at && ` · ${t('race.voided')}`}
                </div>
              </div>
              {mayVoid && !m.voided_at && (
                <button type="button" className="link" onClick={() => void voidMatch(m.id)}>{t('race.undo')}</button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
